// tiendaApi — capa de datos de la tienda v5 (catálogo público de compra suelta).
// Fuente: RPC public.tienda_get_productos() — SECURITY DEFINER con EXECUTE para 'anon', así que
// funciona para el visitante público (la tabla public.productos tiene RLS que bloquea el SELECT
// anónimo; por eso NO se consulta la tabla directo). La RPC ya devuelve la ficha tienda completa
// y el ahorro_pct precomputado. Fotos: bucket público Storage 'productos-fotos' → imagen_url.
//
// La RPC NO trae etiquetas_urls (galería extra), atributos booleanos ni created_at → la galería
// muestra solo la foto principal y no hay badge "nuevo"/orden "recién sumados" (necesitarían que
// el backend los agregue a la RPC — deuda para Fase 5+).
//
// Cache: promise memoizada a nivel módulo → un solo fetch por carga de página, compartido entre
// Catalogo y ProductoDetalle. Un refresh de pestaña re-fetchea (esperado). Si el fetch falla, se
// resetea la cache para permitir reintentar.
import { supabase } from './supabase';

// Mapa label + emoji (fallback de foto) para el enum producto_categoria (17 valores).
export const CATEGORIA_META = {
  lavarropa:        { label: 'Lavarropas',       emoji: '🧺' },
  cocina:           { label: 'Cocina',           emoji: '🍽️' },
  bano:             { label: 'Baño',             emoji: '🚿' },
  papel:            { label: 'Papel',            emoji: '🧻' },
  higiene:          { label: 'Higiene',          emoji: '🧴' },
  manos:            { label: 'Manos',            emoji: '🧼' },
  desinfectantes:   { label: 'Desinfectantes',   emoji: '🦠' },
  bolsas:           { label: 'Bolsas',           emoji: '🛍️' },
  accesorios:       { label: 'Accesorios',       emoji: '🧹' },
  otros:            { label: 'Otros',            emoji: '📦' },
  multiuso:         { label: 'Multiuso',         emoji: '🧽' },
  pisos:            { label: 'Pisos',            emoji: '🌊' },
  vidrios:          { label: 'Vidrios',          emoji: '🪞' },
  lavandinas:       { label: 'Lavandinas',       emoji: '💧' },
  panuelos:         { label: 'Pañuelos',         emoji: '🤧' },
  cuidado_personal: { label: 'Cuidado personal', emoji: '🧴' },
  ambientadores:    { label: 'Ambientadores',    emoji: '🌸' },
};

export const categoriaLabel = (cat) => CATEGORIA_META[cat]?.label || 'Productos';
export const categoriaEmoji = (cat) => CATEGORIA_META[cat]?.emoji || '🧴';

// Orden curado de categorías en la tienda (chips + secciones de la vista "Todos").
// Lavarropas y Papel primero (pedido de Nico); después el núcleo de limpieza y, al final,
// bolsas/accesorios/otros. Lo que no esté acá cae al final. Para reordenar: tocar este array.
export const ORDEN_CATEGORIAS = [
  'lavarropa', 'papel', 'multiuso', 'cocina', 'manos',
  'bano', 'pisos', 'vidrios', 'lavandinas', 'desinfectantes',
  'ambientadores', 'cuidado_personal', 'higiene', 'panuelos',
  'bolsas', 'accesorios', 'otros',
];
export const ordenCategoria = (cat) => {
  const i = ORDEN_CATEGORIAS.indexOf(cat);
  return i === -1 ? ORDEN_CATEGORIAS.length : i;
};

// Normaliza una fila de la RPC tienda_get_productos() a la forma que consumen los componentes v5.
function derivar(row) {
  const precio = Number(row.precio_publico);
  const mercado = row.precio_mercado != null ? Number(row.precio_mercado) : null;
  const salePct = Number(row.ahorro_pct) || 0;         // ya viene precomputado por la RPC
  const tachado = mercado && mercado > precio ? mercado : null;
  const stock = Number(row.stock ?? 0);
  return {
    ...row,
    precio,
    tachado,                                   // number | null (precio anterior)
    sale: salePct > 0 ? `-${salePct}%` : undefined,
    salePct,                                   // number (0 si no hay descuento)
    disponible: stock,                         // stock vendible
    agotado: stock <= 0,
    badge: row.destacado ? 'top' : undefined,  // "nuevo" no disponible (la RPC no trae created_at)
    beneficios: Array.isArray(row.beneficios) ? row.beneficios : [],
    etiquetas_urls: Array.isArray(row.etiquetas_urls) ? row.etiquetas_urls : [], // RPC no trae → []
  };
}

let _cache = null; // Promise<Producto[]> | null

// Trae el catálogo tienda (cacheado, vía RPC anon-safe). Orden "curado": destacado → orden → nombre.
export function fetchProductosTienda() {
  if (_cache) return _cache;
  _cache = (async () => {
    const { data, error } = await supabase.rpc('tienda_get_productos');
    if (error) throw error;
    const list = (data || []).map(derivar);
    list.sort((a, b) =>
      (b.destacado === true) - (a.destacado === true) ||
      (a.orden ?? 0) - (b.orden ?? 0) ||
      a.nombre.localeCompare(b.nombre, 'es')
    );
    return list;
  })();
  // Si falla, limpiar la cache para permitir reintento (no dejar cacheada la promesa rechazada).
  _cache.catch(() => { _cache = null; });
  return _cache;
}

// ── Kits con sus productos reales (fotos + SKU) ──────────────────────────────
// Fuente: RPC public.cliente_get_kits_con_productos() — SECURITY DEFINER anon-safe. Devuelve los 6
// kits, cada uno con productos[]: { sku, marca, nombre, cantidad, es_regalo, imagen_url, presentacion,
// precio_mercado, precio_publico }. Lo usa KitDetalle para mostrar los productos internos con FOTO REAL
// + SKU (informativo; el kit se compra entero por el CTA principal, NO ítem por ítem).
//
// El código del kit en la DB usa guion_bajo (hogar_plus); las rutas v5 usan guion (hogar-plus).
// normalizarCodigoKit() salva esa diferencia al hacer el lookup.
export const normalizarCodigoKit = (c) => String(c || '').replace(/-/g, '_');

let _cacheKits = null; // Promise<Record<codigo, KitConProductos>> | null

export function fetchKitsConProductos() {
  if (_cacheKits) return _cacheKits;
  _cacheKits = (async () => {
    const { data, error } = await supabase.rpc('cliente_get_kits_con_productos');
    if (error) throw error;
    const map = {};
    for (const k of (data || [])) if (k && k.codigo) map[k.codigo] = k;
    return map;
  })();
  _cacheKits.catch(() => { _cacheKits = null; });
  return _cacheKits;
}

// Chips de categoría a partir de las categorías PRESENTES en la data (con conteo).
// 'Todos' primero; el resto en el orden curado (ORDEN_CATEGORIAS). Nunca deja productos fuera.
export function categoriasPresentes(productos) {
  const counts = {};
  for (const p of productos) counts[p.categoria] = (counts[p.categoria] || 0) + 1;
  const cats = Object.keys(counts)
    .sort((a, b) => ordenCategoria(a) - ordenCategoria(b))
    .map((key) => ({ key, label: categoriaLabel(key), emoji: categoriaEmoji(key), count: counts[key] }));
  return [{ key: 'todos', label: 'Todos', emoji: '', count: productos.length }, ...cats];
}
